import axios, { AxiosProgressEvent } from 'axios';
import { config } from '../config/index';
import { authService } from './auth';

// API pour les abonnements/Stripe (thor-api)
const api = axios.create({
  baseURL: config.api.endpoint,
  headers: {
    'Content-Type': 'application/json'
  }
});

// API pour la génération de titres (demo-thor-api)
const titreApi = axios.create({
  baseURL: config.api.titreEndpoint,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Add auth token to requests
const addAuthToken = (axiosConfig: any) => {
  const token = authService.getToken();
  if (token) {
    axiosConfig.headers.Authorization = `Bearer ${token}`;
  }
  return axiosConfig;
};

api.interceptors.request.use(addAuthToken);
titreApi.interceptors.request.use(addAuthToken);

// Handle auth errors
const handleAuthError = (error: any) => {
  if (error.response?.status === 401) {
    authService.logout();
  }
  return Promise.reject(error);
};

api.interceptors.response.use((response) => response, handleAuthError);
titreApi.interceptors.response.use((response) => response, handleAuthError);

export interface UploadResponse {
  success: boolean;
  job_id: string;
  status: string;
  message: string;
  file_info?: {
    name: string;
    format: string;
    size: number;
    text_length: number;
    truncated: boolean;
  };
}

export interface JobStatus {
  job_id: string;
  status: 'QUEUED' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  result?: {
    titre: string;
    resume: string;
  };
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export const apiService = {
  async uploadFile(
    file: File,
    onProgress?: (event: AxiosProgressEvent) => void
  ): Promise<UploadResponse> {
    // Convert file to base64
    const base64 = await fileToBase64(file);

    // Utilise titreApi (demo-thor-api) pour l'upload
    const response = await titreApi.post<UploadResponse>(
      '/upload',
      {
        file: base64,
        filename: file.name
      },
      {
        onUploadProgress: onProgress
      }
    );

    return response.data;
  },

  async getJobStatus(jobId: string): Promise<JobStatus> {
    // Utilise titreApi (demo-thor-api) pour le statut des jobs
    const response = await titreApi.get<JobStatus>(`/job/${jobId}`);
    return response.data;
  },

  async getResults(userGroup?: string): Promise<JobStatus[]> {
    const params = userGroup ? { user_group: userGroup } : {};
    // Utilise titreApi (demo-thor-api) pour les résultats
    const response = await titreApi.get<JobStatus[]>('/results', { params });
    return response.data;
  },

  async testAuth(): Promise<any> {
    // Utilise titreApi (demo-thor-api) pour le test d'auth
    const response = await titreApi.get('/auth-test');
    return response.data;
  }
};

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result as string;
      // Remove data URL prefix (e.g., "data:text/plain;base64,")
      const base64Data = base64.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = (error) => reject(error);
  });
}